import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle, Send, ArrowLeft, Plus, X,
  Check, CheckCheck, Search, ChevronRight, Users, Trash2
} from 'lucide-react';
import BottomNav from '@/components/BottomNav';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KELAS_MAP = { D: "I'dad", C: 'Uula', B: 'Wustha', A: 'Ulya', M: 'Ali/Mahir' };

function playNotifSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(500, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(); osc.stop(ctx.currentTime + 0.3);
  } catch(e) {}
}

// Ensure UTC interpretation when server sends bare ISO strings without timezone
function parseDate(iso) {
  if (!iso) return null;
  if (typeof iso !== 'string') return new Date(iso);
  // Python isoformat() with timezone.utc includes +00:00; handle bare datetimes
  if (iso.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(iso)) return new Date(iso);
  return new Date(iso + 'Z');
}

function fmtTime(iso) {
  const d = parseDate(iso);
  if (!d) return '';
  return d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
}

function fmtDateLabel(iso) {
  const d = parseDate(iso);
  if (!d) return '';
  const today = new Date();
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Hari ini';
  if (d.toDateString() === yest.toDateString()) return 'Kemarin';
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function ChatPage() {
  const { user } = useAuth();

  const userId = user?.id || user?._id;

  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loadingPart, setLoadingPart] = useState(false);
  const [mobileView, setMobileView] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [autoStarted, setAutoStarted] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteHistory, setConfirmDeleteHistory] = useState(false);
  const [deletingHistory, setDeletingHistory] = useState(false);

  const endRef = useRef(null);
  const prevCountRef = useRef(0);
  const pollingRef = useRef(null);
  const inputRef = useRef(null);
  const convIdRef = useRef(null);

  const loadConvs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chat/conversations`, { withCredentials: true });
      setConversations(r.data);
      return r.data;
    } catch(e) { return []; }
  }, []);

  const loadMsgs = useCallback(async (convId, isPolling = false) => {
    if (!convId) return;
    try {
      const r = await axios.get(`${API}/chat/conversations/${convId}/messages`, { withCredentials: true });
      const msgs = r.data;
      if (isPolling && msgs.length > prevCountRef.current) {
        const incoming = msgs.slice(prevCountRef.current).filter(m => m.sender_id !== userId);
        if (incoming.length > 0) playNotifSound();
      }
      prevCountRef.current = msgs.length;
      setMessages(msgs);
      if (msgs.some(m => m.sender_id !== userId && !m.read_at)) {
        axios.put(`${API}/chat/conversations/${convId}/read`, {}, { withCredentials: true })
          .then(() => setConversations(prev => prev.map(c => {
            if (c.id !== convId) return c;
            return { ...c, unread_guru: user?.role === 'guru' ? 0 : c.unread_guru, unread_santri: user?.role === 'santri' ? 0 : c.unread_santri };
          }))).catch(() => {});
      }
    } catch(e) {}
  }, [userId, user]);

  useEffect(() => {
    setLoading(true);
    loadConvs().finally(() => setLoading(false));
  }, [loadConvs]);

  // Santri: auto-start conversation with their guru
  useEffect(() => {
    if (user?.role !== 'santri' || autoStarted || loading) return;
    setAutoStarted(true);
    axios.get(`${API}/chat/participants`, { withCredentials: true }).then(async r => {
      if (r.data?.length === 1) {
        try {
          await axios.post(`${API}/chat/conversations`, { participant_user_id: r.data[0].user_id }, { withCredentials: true });
          loadConvs();
        } catch(e) {}
      }
    }).catch(() => {});
  }, [user, autoStarted, loading, loadConvs]);

  const selectConv = useCallback(async (conv) => {
    setSelectedConv(conv);
    setMessages([]);
    prevCountRef.current = 0;
    convIdRef.current = conv.id;
    setMobileView('chat');
    await loadMsgs(conv.id);
    setTimeout(() => inputRef.current?.focus(), 300);
  }, [loadMsgs]);

  useEffect(() => {
    if (!selectedConv) return;
    const poll = async () => {
      if (convIdRef.current) await loadMsgs(convIdRef.current, true);
      loadConvs();
    };
    pollingRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollingRef.current);
  }, [selectedConv, loadMsgs, loadConvs]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleNewChat = async () => {
    setShowNewChat(true); setLoadingPart(true);
    try { const r = await axios.get(`${API}/chat/participants`, { withCredentials: true }); setParticipants(r.data); }
    catch(e) {}
    setLoadingPart(false);
  };

  const handleStartChat = async (uid) => {
    try {
      const r = await axios.post(`${API}/chat/conversations`, { participant_user_id: uid }, { withCredentials: true });
      setShowNewChat(false);
      await loadConvs();
      selectConv(r.data);
    } catch(e) {}
  };

  const handleSend = async () => {
    if (!newMsg.trim() || !selectedConv || sending) return;
    setSending(true);
    const text = newMsg.trim();
    setNewMsg('');
    try {
      await axios.post(`${API}/chat/conversations/${selectedConv.id}/messages`, { body: text }, { withCredentials: true });
      await loadMsgs(selectedConv.id);
      loadConvs();
      inputRef.current?.focus();
    } catch(e) { setNewMsg(text); }
    setSending(false);
  };

  const handleDeleteMsg = async (msgId) => {
    if (!selectedConv) return;
    setDeletingId(msgId);
    try {
      await axios.delete(`${API}/chat/conversations/${selectedConv.id}/messages/${msgId}`, { withCredentials: true });
      setMessages(prev => prev.filter(m => m.id !== msgId));
      setConfirmDeleteId(null);
      loadConvs();
    } catch(e) {}
    setDeletingId(null);
  };

  const handleDeleteConvHistory = async () => {
    if (!selectedConv) return;
    setDeletingHistory(true);
    try {
      await axios.delete(`${API}/chat/conversations/${selectedConv.id}/messages`, { withCredentials: true });
      setMessages([]);
      setConfirmDeleteHistory(false);
      loadConvs();
    } catch(e) {}
    setDeletingHistory(false);
  };

  const handleKeyDown = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const getOtherName = conv => !conv ? '' : (user?.role === 'guru' ? conv.santri_name : conv.guru_name);
  const getUnread = conv => user?.role === 'guru' ? (conv.unread_guru || 0) : (conv.unread_santri || 0);
  const filteredConvs = conversations.filter(c => getOtherName(c).toLowerCase().includes(searchQuery.toLowerCase()));

  const groupedMsgs = messages.reduce((acc, msg) => {
    const label = fmtDateLabel(msg.created_at);
    if (!acc[label]) acc[label] = [];
    acc[label].push(msg);
    return acc;
  }, {});

  return (
    <div className="bg-gray-50 overflow-hidden" style={{ height: '100dvh' }} data-testid="chat-page">

      {/* ── MOBILE LIST ── */}
      <div className={`md:hidden flex flex-col ${mobileView === 'chat' ? 'hidden' : 'flex'}`} style={{ height: '100dvh' }}>
        <div className="bg-gradient-to-r from-emerald-950 to-emerald-800 text-white px-5 pb-4 flex items-center justify-between flex-shrink-0"
          style={{ paddingTop: 'max(48px, env(safe-area-inset-top))' }}>
          <div>
            <h1 className="text-xl font-bold">Chat</h1>
            <p className="text-xs text-emerald-300 mt-0.5">{user?.role === 'guru' ? 'Santri halaqah anda' : 'Guru halaqah anda'}</p>
          </div>
          {user?.role === 'guru' && (
            <button onClick={handleNewChat} data-testid="new-chat-btn"
              className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center hover:bg-white/30 transition-all">
              <Plus size={20} />
            </button>
          )}
        </div>
        <div className="px-4 py-2 bg-white border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input data-testid="chat-search-input" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Cari percakapan..."
              className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-emerald-400" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)' }}>
          {loading ? <LoadingSpinner /> : filteredConvs.length === 0
            ? <EmptyState user={user} onNewChat={handleNewChat} />
            : filteredConvs.map(c => (
              <ConvItem key={c.id} conv={c} isSelected={false} onClick={() => selectConv(c)} getOtherName={getOtherName} getUnread={getUnread} />
            ))}
        </div>
        <BottomNav />
      </div>

      {/* ── MOBILE CHAT ── */}
      <div className={`md:hidden flex flex-col ${mobileView === 'list' ? 'hidden' : 'flex'}`} style={{ height: '100dvh' }}>
        <ChatHeader conv={selectedConv} getOtherName={getOtherName}
          onBack={() => {
            setMobileView('list');
            setSelectedConv(null);
            convIdRef.current = null;
            clearInterval(pollingRef.current);
            setConfirmDeleteId(null);
          }}
          onDeleteHistory={user?.role === 'guru' ? () => setConfirmDeleteHistory(true) : null}
          userRole={user?.role} />
        <div className="flex-1 overflow-y-auto px-4 py-2 bg-[#efeae2]" style={{ overscrollBehavior: 'contain' }}>
          <MsgList groupedMsgs={groupedMsgs} userId={userId} endRef={endRef}
            onMsgLongPress={id => setConfirmDeleteId(id)} />
        </div>
        <InputArea newMsg={newMsg} setNewMsg={setNewMsg} onSend={handleSend}
          onKeyDown={handleKeyDown} sending={sending} inputRef={inputRef} />
      </div>

      {/* ── DESKTOP TWO-COLUMN ── */}
      <div className="hidden md:flex" style={{ height: '100dvh' }}>
        <div className="w-72 flex-shrink-0 flex flex-col bg-white border-r border-gray-100 shadow-sm">
          <div className="px-4 pt-6 pb-3 border-b border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-bold text-gray-800 text-lg">Chat</h2>
              {user?.role === 'guru' && (
                <button onClick={handleNewChat} data-testid="new-chat-btn-desktop"
                  className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center hover:bg-emerald-200 transition-all">
                  <Plus size={16} className="text-emerald-700" />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400">{user?.role === 'guru' ? 'Santri halaqah anda' : 'Guru halaqah anda'}</p>
          </div>
          <div className="px-3 py-2 border-b border-gray-50">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cari..."
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-50 border border-gray-100 rounded-lg focus:outline-none focus:border-emerald-400" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? <LoadingSpinner /> : filteredConvs.length === 0
              ? <EmptyState user={user} onNewChat={handleNewChat} compact />
              : filteredConvs.map(c => (
                <ConvItem key={c.id} conv={c} isSelected={selectedConv?.id === c.id} onClick={() => selectConv(c)} getOtherName={getOtherName} getUnread={getUnread} />
              ))}
          </div>
        </div>
        <div className="flex-1 flex flex-col min-w-0">
          {selectedConv ? (
            <>
              <ChatHeader conv={selectedConv} getOtherName={getOtherName} desktop
                onDeleteHistory={user?.role === 'guru' ? () => setConfirmDeleteHistory(true) : null}
                userRole={user?.role} />
              <div className="flex-1 overflow-y-auto px-6 py-3 bg-[#efeae2]" style={{ overscrollBehavior: 'contain' }}>
                <MsgList groupedMsgs={groupedMsgs} userId={userId} endRef={endRef}
                  onMsgLongPress={id => setConfirmDeleteId(id)} />
              </div>
              <InputArea newMsg={newMsg} setNewMsg={setNewMsg} onSend={handleSend}
                onKeyDown={handleKeyDown} sending={sending} inputRef={inputRef} />
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8 bg-[#efeae2]">
              <div className="w-20 h-20 rounded-3xl bg-white shadow-sm flex items-center justify-center mb-4">
                <MessageCircle size={36} className="text-emerald-400" />
              </div>
              <p className="text-base font-semibold text-gray-600 mb-1">Pilih Percakapan</p>
              <p className="text-sm text-gray-400">Pilih di kiri atau mulai chat baru</p>
            </div>
          )}
        </div>
      </div>

      {/* ── NEW CHAT MODAL ── */}
      <AnimatePresence>
        {showNewChat && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[70] flex items-end md:items-center md:justify-center"
            onClick={e => e.target === e.currentTarget && setShowNewChat(false)}>
            <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="w-full md:w-96 bg-white rounded-t-3xl md:rounded-2xl max-h-[70vh] flex flex-col shadow-2xl">
              <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-100">
                <h3 className="font-semibold text-gray-800">Mulai Percakapan Baru</h3>
                <button onClick={() => setShowNewChat(false)} data-testid="new-chat-close-btn"
                  className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <X size={18} className="text-gray-500" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {loadingPart ? <LoadingSpinner /> : participants.length === 0 ? (
                  <div className="text-center py-8">
                    <Users size={32} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">
                      {user?.role === 'guru' ? 'Belum ada santri di halaqah anda' : 'Guru halaqah belum ditentukan'}
                    </p>
                  </div>
                ) : participants.map(p => (
                  <button key={p.user_id} data-testid={`participant-item-${p.user_id}`}
                    onClick={() => handleStartChat(p.user_id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-emerald-50 rounded-xl transition-all text-left mb-1">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {p.foto ? <img src={p.foto} alt={p.name} className="w-full h-full object-cover" />
                        : <span className="text-emerald-700 font-bold text-sm">{p.name?.charAt(0)}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{p.name}</p>
                      <p className="text-xs text-gray-400">
                        {p.nis ? `NIS: ${p.nis}` : ''}{p.kelas ? ` · ${KELAS_MAP[p.kelas] || p.kelas}` : ''}{p.bidang || ''}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── DELETE CONFIRMATION SHEET ── */}
      <AnimatePresence>
        {confirmDeleteId && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/25 z-[80]"
            onClick={() => !deletingId && setConfirmDeleteId(null)}>
            <motion.div
              initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl px-5 pt-4"
              style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
              onClick={e => e.stopPropagation()}>
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Trash2 size={20} className="text-red-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Hapus Pesan?</p>
                  <p className="text-xs text-gray-400 mt-0.5">Pesan akan dihapus permanen dan tidak bisa dikembalikan</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button data-testid="delete-cancel-btn"
                  onClick={() => setConfirmDeleteId(null)}
                  disabled={!!deletingId}
                  className="flex-1 py-3.5 bg-gray-100 text-gray-700 rounded-2xl text-sm font-semibold disabled:opacity-50 transition-all">
                  Batal
                </button>
                <button data-testid="delete-confirm-btn"
                  onClick={() => handleDeleteMsg(confirmDeleteId)}
                  disabled={!!deletingId}
                  className="flex-1 py-3.5 bg-red-500 hover:bg-red-600 text-white rounded-2xl text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2 transition-all">
                  {deletingId
                    ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Menghapus...</>
                    : <><Trash2 size={15} /> Ya, Hapus</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── HAPUS RIWAYAT PERCAKAPAN SHEET ── */}
      <AnimatePresence>
        {confirmDeleteHistory && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/25 z-[80]"
            onClick={() => !deletingHistory && setConfirmDeleteHistory(false)}>
            <motion.div
              initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl px-5 pt-4"
              style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
              onClick={e => e.stopPropagation()}>
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Trash2 size={20} className="text-red-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Hapus Riwayat Percakapan?</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Semua pesan dengan <b>{selectedConv ? getOtherName(selectedConv) : '—'}</b> akan dihapus permanen
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button data-testid="cancel-delete-history-btn"
                  onClick={() => setConfirmDeleteHistory(false)}
                  disabled={!!deletingHistory}
                  className="flex-1 py-3.5 bg-gray-100 text-gray-700 rounded-2xl text-sm font-semibold disabled:opacity-50 transition-all">
                  Batal
                </button>
                <button data-testid="confirm-delete-history-btn"
                  onClick={handleDeleteConvHistory}
                  disabled={!!deletingHistory}
                  className="flex-1 py-3.5 bg-red-500 hover:bg-red-600 text-white rounded-2xl text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2 transition-all">
                  {deletingHistory
                    ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Menghapus...</>
                    : <><Trash2 size={15} /> Hapus Semua Pesan</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-10">
      <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function EmptyState({ user, onNewChat, compact = false }) {
  return (
    <div className={`flex flex-col items-center justify-center text-center ${compact ? 'py-10 px-4' : 'py-16 px-8'}`}>
      <MessageCircle size={compact ? 28 : 44} className="text-gray-300 mb-3" />
      <p className={`font-semibold text-gray-500 ${compact ? 'text-sm' : 'text-base'} mb-1`}>Belum ada chat</p>
      <p className="text-xs text-gray-400 mb-4">
        {user?.role === 'guru' ? 'Mulai chat dengan santri halaqah anda' : 'Menghubungkan ke guru halaqah anda...'}
      </p>
      {user?.role === 'guru' && (
        <button onClick={onNewChat} data-testid="start-chat-empty-btn"
          className="px-4 py-2 bg-emerald-500 text-white text-sm font-semibold rounded-xl hover:bg-emerald-600 transition-all flex items-center gap-1.5">
          <Plus size={14} /> Mulai Chat
        </button>
      )}
    </div>
  );
}

function ChatHeader({ conv, getOtherName, onBack, onDeleteHistory, desktop = false, userRole }) {
  if (!conv) return null;
  const name = getOtherName(conv);
  const subtitle = userRole === 'guru' ? 'Santri Halaqah' : 'Guru Halaqah';
  return (
    <div className="flex items-center gap-3 px-4 pb-3 bg-emerald-900 text-white shadow-sm flex-shrink-0"
      style={{ paddingTop: desktop ? '12px' : 'max(48px, env(safe-area-inset-top))' }}>
      {!desktop && (
        <button onClick={onBack} data-testid="chat-back-btn"
          className="p-1.5 hover:bg-white/20 rounded-lg flex-shrink-0 active:scale-95 transition-all">
          <ArrowLeft size={20} />
        </button>
      )}
      <div className="w-9 h-9 rounded-full bg-emerald-700 flex items-center justify-center flex-shrink-0">
        <span className="text-white font-bold text-sm">{name?.charAt(0)?.toUpperCase()}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate" data-testid="chat-partner-name">{name}</p>
        <p className="text-xs text-emerald-300">{subtitle}</p>
      </div>
      {onDeleteHistory && (
        <button
          data-testid="delete-history-btn"
          onClick={onDeleteHistory}
          className="p-1.5 hover:bg-red-500/30 rounded-lg flex-shrink-0 active:scale-95 transition-all"
          title="Hapus Riwayat Percakapan"
        >
          <Trash2 size={18} />
        </button>
      )}
    </div>
  );
}

function ConvItem({ conv, isSelected, onClick, getOtherName, getUnread }) {
  const name = getOtherName(conv);
  const unread = getUnread(conv);
  return (
    <button data-testid={`conv-item-${conv.id}`} onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-emerald-50/60 transition-all text-left border-b border-gray-50 ${isSelected ? 'bg-emerald-50' : ''}`}>
      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
        <span className="text-white font-bold text-base">{name?.charAt(0)?.toUpperCase()}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <p className={`text-sm truncate ${unread > 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>{name}</p>
          <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">{fmtTime(conv.last_message_at)}</span>
        </div>
        <div className="flex items-center justify-between">
          <p className={`text-xs truncate ${unread > 0 ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
            {conv.last_message || 'Belum ada pesan'}
          </p>
          {unread > 0 && (
            <span data-testid={`unread-badge-${conv.id}`}
              className="ml-2 flex-shrink-0 min-w-[18px] h-[18px] bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function MsgList({ groupedMsgs, userId, endRef, onMsgLongPress }) {
  const entries = Object.entries(groupedMsgs);
  if (entries.length === 0) return (
    <div className="flex items-center justify-center h-24">
      <p className="text-sm text-gray-500">Belum ada pesan. Mulailah percakapan!</p>
    </div>
  );
  return (
    <>
      {entries.map(([label, msgs]) => (
        <div key={label}>
          <div className="flex justify-center my-3">
            <span className="text-[11px] text-gray-500 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm">{label}</span>
          </div>
          {msgs.map((msg, i) => (
            <MsgBubble key={msg.id || i} msg={msg} isOwn={msg.sender_id === userId}
              onLongPress={() => onMsgLongPress(msg.id)} />
          ))}
        </div>
      ))}
      <div ref={endRef} className="h-4" />
    </>
  );
}

function MsgBubble({ msg, isOwn, onLongPress }) {
  const pressTimer = useRef(null);

  const startPress = () => {
    if (!isOwn) return;
    pressTimer.current = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(40);
      onLongPress();
    }, 500);
  };
  const endPress = () => clearTimeout(pressTimer.current);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}
      className={`flex mb-1.5 ${isOwn ? 'justify-end' : 'justify-start'}`}
      data-testid={`msg-bubble-${msg.id}`}
      onTouchStart={startPress}
      onTouchEnd={endPress}
      onTouchMove={endPress}
      onMouseDown={startPress}
      onMouseUp={endPress}
      onMouseLeave={endPress}
      onContextMenu={isOwn ? (e) => { e.preventDefault(); onLongPress(); } : undefined}
    >
      <div className={`max-w-[78%] md:max-w-[62%] ${isOwn ? '' : 'flex flex-col items-start'}`}>
        {!isOwn && (
          <span className="text-[10px] text-emerald-700 font-medium ml-1 mb-0.5">{msg.sender_name}</span>
        )}
        <div className={`px-3.5 py-2 rounded-2xl shadow-sm select-none ${
          isOwn ? 'bg-emerald-600 text-white rounded-br-sm' : 'bg-white text-gray-800 rounded-bl-sm'
        }`}>
          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{msg.body}</p>
        </div>
        <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end mr-1' : 'ml-1'}`}>
          <span className="text-[10px] text-gray-500">{fmtTime(msg.created_at)}</span>
          {isOwn && (msg.read_at
            ? <CheckCheck size={12} className="text-emerald-400" />
            : <Check size={12} className="text-gray-400" />)}
        </div>
      </div>
    </motion.div>
  );
}

function InputArea({ newMsg, setNewMsg, onSend, onKeyDown, sending, inputRef }) {
  return (
    <div className="flex-shrink-0 flex items-end gap-2 px-3 py-2.5 bg-[#f0f2f5] border-t border-gray-200"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 10px), 10px)' }}>
      <div className="flex-1 bg-white rounded-3xl px-4 py-2.5 shadow-sm border border-gray-100 flex items-end min-w-0">
        <textarea
          ref={inputRef}
          data-testid="chat-input"
          value={newMsg}
          onChange={e => setNewMsg(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ketik pesan..."
          rows={1}
          className="flex-1 w-full bg-transparent text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none max-h-28 leading-relaxed"
        />
      </div>
      <button
        data-testid="chat-send-btn"
        onClick={onSend}
        disabled={!newMsg.trim() || sending}
        className="w-11 h-11 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 hover:bg-emerald-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 shadow-md">
        {sending
          ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          : <Send size={17} className="text-white" style={{ marginLeft: '1px' }} />}
      </button>
    </div>
  );
}
